// Step 6 of the campaign builder: budget, dates, flights and the agency margin.

import { Icons, fmt } from '@/components/primitives';
import { MetaDeliveryPolicy } from '@/components/meta-delivery-policy';
import { RequiredField } from '@/components/required-field';
import { LockBanner, LockHint, LockedField } from '@/components/lock-primitives';
import { Section } from '@/components/wizard-section';
import { fmtDay } from '@/lib/datetime';
import { flightValidationIssues } from '@/lib/campaign-flights';
import { FlightsEditor } from './create-flights-editor';
import type { CreateFlowModel } from './create-flow-model';

export function ScheduleStep({ model }: { model: CreateFlowModel }) {
  const { campaign, wasLive, flightsDelivering, totalSpent, totalSpentFailed, flightSpend, todayStr, today, production, channel,
    metaContract, budget, setBudget, days, maxEndISO, startDate, setStartDate, endDate, setEndDate,
    schConfirmed, setSchConfirmed, markup, setMarkup, editedFlights, setEditedFlights, flights,
    setFlights, required, open, setOpen, toggle, budgetFlash, setBudgetFlash, handleDuplicate,
    extendAvailable, handleOpenExtend,
  } = model;
  return (
    <Section n="6" title="Schedule & Budget & Agency Margin" summary={(() => {
      const parts = [
        // The exact figure, as the review list and the launch dialog give it (F5 UX P3).
        fmt.$full(budget),
        `${fmtDay(startDate)} → ${fmtDay(endDate)}`,
        `Agency margin ${markup ? 'ON' : 'OFF'}`,
      ];
      return (schConfirmed ? 'Confirmed · ' : '') + parts.join(' · ');
    })()} open={open.sched} done={schConfirmed} onToggle={()=>toggle('sched')} locked={wasLive} lockNotice={
      <LockBanner
        className="locked-banner--section"
        onDuplicate={() => { void handleDuplicate(); }}
        action={extendAvailable ? { label: 'Extend flight…', onClick: handleOpenExtend } : undefined}
      >
        {extendAvailable
          ? 'Dates, budget and the agency margin are frozen after the publishing handoff. Extend the flight to run it again, or duplicate to create a draft with different terms.'
          : 'Dates, budget and the agency margin are frozen after the publishing handoff. Duplicate to create a draft with different terms.'}
      </LockBanner>
    }>
      <MetaDeliveryPolicy contract={metaContract} scopes={['ad_set']} />
      <div className="grid-3 mb-3">
        {wasLive ? (
          /* No save path exists for a published campaign's budget (every
             plan write is draft-only), so the field must not look
             editable — the floor hint it used to carry described a save
             nobody could make. */
          <LockedField
            label="Client budget (gross)"
            value={fmt.$full(budget)}
            sublabel={totalSpent !== null
              ? `${fmt.$full(totalSpent)} spent so far`
              : totalSpentFailed ? 'Spend could not be read' : 'Spend not reported yet'}
            reason={`Locked after publishing handoff · ${fmtDay(startDate)}.`}
          />
        ) : (
        <RequiredField
          fieldKey="campaign-budget"
          message={required.messages['campaign-budget']}
          attempt={required.attempt}
          className="field"
        >
          <label className="field-lbl" style={{display:'flex',alignItems:'center',gap:8}}>
            <span>Client budget (gross)</span>
          </label>
          <input
            className={`input mono${budgetFlash ? ' floor-flash' : ''}`}
            value={'$' + budget.toLocaleString()}
            onChange={e=>{
              const n=parseInt(e.target.value.replace(/[^\d]/g,''));
              if(!isNaN(n)) {
                required.clear('campaign-budget');
                required.clear('campaign-flights');
                setBudget(n);
              }
            }}
          />
          <div className="field-hint">≈ ${Math.round(budget/days).toLocaleString()}/day gross · suggested for {production?.title || 'production'}</div>
        </RequiredField>
        )}
        <RequiredField
          fieldKey="campaign-start-date"
          message={required.messages['campaign-start-date']}
          attempt={required.attempt}
          className="field"
        >
          <label className="field-lbl">Start date</label>
          <input type="date" className={`input mono ${wasLive ? 'input-locked' : ''}`} value={startDate} min={wasLive ? startDate : todayStr} max={wasLive ? startDate : (endDate || maxEndISO)} onChange={e=>{
            if (!wasLive) {
              required.clear('campaign-start-date');
              required.clear('campaign-flights');
              setStartDate(e.target.value);
            }
          }} readOnly={wasLive} title={wasLive ? 'Locked after publishing handoff' : ''}/>
          <div className="field-hint">{wasLive ? <><span style={{color:'var(--ink-2)'}}>🔒</span> Locked after publishing handoff · {fmtDay(startDate)}</> : 'Earliest: today'}</div>
        </RequiredField>
        <RequiredField
          fieldKey="campaign-end-date"
          message={required.messages['campaign-end-date']}
          attempt={required.attempt}
          className="field"
        >
          <label className="field-lbl">End date</label>
          <input type="date" className="input mono" value={endDate} min={startDate || todayStr} max={maxEndISO} onChange={e=>{
            required.clear('campaign-end-date');
            required.clear('campaign-flights');
            setEndDate(e.target.value);
          }}/>
          <div className="field-hint">{days} day{days===1?'':'s'} · max 180 days out</div>
        </RequiredField>
      </div>

      <RequiredField
        fieldKey="campaign-flights"
        message={required.messages['campaign-flights']}
        attempt={required.attempt}
        surface
      >
        <FlightsEditor
          startDate={startDate}
          endDate={endDate}
          totalBudget={budget}
          flights={flights}
          setFlights={(next) => {
            required.clear('campaign-flights');
            setFlights(next);
          }}
          editedFlights={editedFlights}
          setEditedFlights={setEditedFlights}
          wasLive={wasLive}
          delivering={flightsDelivering}
          today={today}
          flightSpend={flightSpend}
        />
      </RequiredField>

      <div className="markup-block">
        {wasLive ? (
          <LockedField
            label="Agency margin"
            value={markup ? 'ON' : 'OFF'}
            sublabel={markup
              ? (channel === 'meta' ? '15% of gross to the agency'
                 : channel === 'ctv' ? '$25 → $30 reported CPM'
                 : channel === 'dsp' ? '$6 → $8 reported CPM'
                 : '—')
              : 'No agency margin · reported CPM is the base rate'}
            reason={`Locked after publishing handoff · ${fmtDay(startDate)}.`}
          />
        ) : (
        <>
        <div className="flex between items-center" style={{gap:12}}>
          <div style={{flex:1}}>
            <div className="field-lbl" style={{margin:0,display:'flex',alignItems:'center'}}>
              <span>Agency margin</span>
              <LockHint/>
            </div>
            <div className="muted" style={{fontSize:11,marginTop:2}}>
              {markup
                ? (channel === 'meta' ? 'Meta · 15% of gross to the agency'
                   : channel === 'ctv' ? 'CTV · reported CPM $30 against a $25 base rate'
                   : channel === 'dsp' ? 'Display · reported CPM $8 against a $6 base rate'
                   : 'Applied per channel · select a channel to preview')
                : 'Off · reported CPM is the base rate and no margin is invoiced'}
            </div>
          </div>
          <label className="markup-toggle" title={markup ? 'Agency margin ON' : 'Agency margin OFF'}>
            <input type="checkbox" checked={markup} onChange={e=>setMarkup(e.target.checked)}/>
            <span className="markup-toggle-track"><span className="markup-toggle-thumb"/></span>
            <span className="mono" style={{fontSize:11,marginLeft:8,color: markup ? 'var(--good)' : 'var(--ink-3)'}}>{markup ? 'ON' : 'OFF'}</span>
          </label>
        </div>
        <div className="markup-grid markup-grid-single">
          {channel === 'meta' && (
            <div className={`markup-cell markup-cell-active ${!markup ? 'markup-cell-off' : ''}`}>
              <div className="markup-cell-ch">Meta</div>
              <div className="markup-cell-val">{markup ? '15%' : '0%'}</div>
              <div className="markup-cell-hint">agency margin, as a share of gross</div>
            </div>
          )}
          {channel === 'ctv' && (
            <div className={`markup-cell markup-cell-active ${!markup ? 'markup-cell-off' : ''}`}>
              <div className="markup-cell-ch">CTV</div>
              <div className="markup-cell-val">{markup ? '$25 → $30' : '$25'}</div>
              <div className="markup-cell-hint">reported CPM · impressions are bought at the $25 base</div>
            </div>
          )}
          {channel === 'dsp' && (
            <div className={`markup-cell markup-cell-active ${!markup ? 'markup-cell-off' : ''}`}>
              <div className="markup-cell-ch">Display</div>
              <div className="markup-cell-val">{markup ? '$6 → $8' : '$6'}</div>
              <div className="markup-cell-hint">reported CPM · impressions are bought at the $6 base</div>
            </div>
          )}
          {!channel && (
            <div className="muted" style={{fontSize:11.5,padding:'8px 4px'}}>Select a channel in Section 3 to preview its agency margin rule.</div>
          )}
        </div>
        </>
        )}
      </div>

      <div className="flex between items-center mt-3" style={{gap:10}}>
        <div className="muted" style={{fontSize:11.5}}>
          {schConfirmed ? <><span style={{color:'var(--good)'}}>{Icons.check}</span> Schedule confirmed. Edit to adjust.</> : 'Review budget and dates, then confirm to continue.'}
        </div>
        {schConfirmed
          ? <button className="btn btn-sm" onClick={()=>setSchConfirmed(false)}>Edit schedule</button>
          : <button
              className="btn btn-primary"
              onClick={()=>{
                const issues = flightValidationIssues(flights, startDate, endDate, budget, { storedStartDate: campaign?.startDate ?? null, todayStr, maxEndISO });
                if (!required.report(issues)) return;
                setSchConfirmed(true);
                setOpen({...open, sched: false, rev: true});
              }}
            >{Icons.check} Confirm schedule</button>}
      </div>
    </Section>
  );
}
